// The invitations half of the staff "Prequalification" tab, shared by the
// Broker and Brokerage detail pages. Everything here is scoped to ONE partner
// site: the record's own brand, passed in as `brandId`.
//
// A PANEL, not a section. It renders a list and its controls — no heading of
// its own (the sub-tab label is the heading) and no send action (the tab owns
// the single "New Prequalification" button, which offers a mailed link and an
// in-person start from one place).
//
// The reads and writes are the SAME endpoints the cross-book Prequalification
// queue uses (`usePrequalificationInvites` / resend / revoke), narrowed by the
// `partnerBrandId` filter. There is no second read model and no partner-scoped
// endpoint here: this is a staff surface, and the staff list is unscoped by
// construction — the narrowing is convenience.
//
// 🔴 MOUNTED BEHIND Prequalification.Read. The tab is gated Brokers.Read /
// Brokerages.Read, which a dozen roles hold; these rows are gated
// Prequalification.Read, which four do. The tab hides this panel's sub-tab
// rather than rendering it into a 403 — see PrequalificationSiteTab.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Ban, Copy, Globe, RotateCw } from "lucide-react";
import type { PrequalInviteDto } from "@/api/prequalification";
import type { PrequalSort } from "@/api/prequalification";
import {
  usePrequalificationInvites,
  useResendPrequalificationInvite,
  useRevokePrequalificationInvite,
} from "@/hooks/prequalification/usePrequalification";
import { useAuth } from "@/hooks/useAuth";
import { Badge, Button, EmptyState, SearchField, SortSelect } from "@/design-system/components/ui";
import {
  PREQUAL_INVITE_STATUS_BADGE,
  isOutstandingInviteStatus,
  isTerminalInviteStatus,
  terminalInviteReason,
} from "@/design-system/lib/prequalInviteStatus";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { useDateFormatter } from "@/utils/datetime";

const PAGE_SIZE = 25;

/** Keystrokes are cheap; a paged server query is not. Matches the debounce the
 *  completions and in-progress panels on this tab use. */
const SEARCH_DEBOUNCE_MS = 300;

/** Display labels only — the two WIRE tokens are single-sourced as
 *  `PrequalSort`. All three panels on this tab show the same pair so one sort
 *  control means one thing everywhere. */
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

/**
 * Display-only stall threshold, mirroring `PrequalInviteTerms.DefaultStallDays`.
 *
 * <b>Not authoritative.</b> The server exposes no stall flag on the invite DTO —
 * the cadence lives on the nudge worker's options and is tunable per
 * environment, so an operator who has retuned it will see this marker disagree
 * with when the reminder actually fires. It is therefore a hint on the row and
 * NEVER an input to anything: the row actions gate on the server's effective
 * status alone.
 */
const STALL_DAYS = 7;
const STALL_MS = STALL_DAYS * 24 * 60 * 60 * 1000;

function isStalled(invite: PrequalInviteDto, nowMs: number): boolean {
  // `Started` is reachable only once a draft has been persisted, so the
  // outstanding set is legitimately thin in most books — that is not a bug and
  // must not be "fixed" with a fallback.
  if (!isOutstandingInviteStatus(invite.status)) return false;
  // Measured from the LAST send: a resend pushes the expiry horizon out and
  // clears the server's nudge stamp, so it restarts the clock here too.
  const lastSent = Date.parse(invite.lastSentAtUtc);
  return !Number.isNaN(lastSent) && nowMs - lastSent > STALL_MS;
}

export interface PrequalSiteInvitesSectionProps {
  ownerKind: "broker" | "brokerage" | "buyerTeam";
  /** The record this panel is mounted on. Accepted for the contract's sake
   *  and deliberately unread: the scope of this panel is the SITE, not the
   *  owner, and an invite carries a brand but no owner. */
  ownerId: string;
  /** The owner's prequalification site, or null when they have none. */
  brandId: string | null;
}

export function PrequalSiteInvitesSection({
  ownerKind,
  brandId,
}: PrequalSiteInvitesSectionProps) {
  // No site means no invitations — not "every invitation in the book". The
  // query lives in the child so it never fires in this branch.
  if (!brandId) {
    return (
      <section data-testid="prequal-site-invites" aria-label="Invitations">
        <EmptyState
          icon={Globe}
          title="No prequalification site"
          description={
            ownerKind === "broker"
              ? "Invitations are sent from a prequalification site. This broker has none, so there is nothing to send from and nothing to track."
              : ownerKind === "brokerage"
                ? "Invitations are sent from a prequalification site. This firm has none, so there is nothing to send from and nothing to track."
                : "Invitations are sent from a prequalification site. This team has none, so there is nothing to send from and nothing to track."
          }
        />
      </section>
    );
  }

  return <InvitesPanel brandId={brandId} />;
}

function InvitesPanel({ brandId }: { brandId: string }) {
  const { date } = useDateFormatter();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("Prequalification.Manage");

  const [page, setPage] = useState(1);
  // `rawSearch` is what the box shows; `q` is what the server has been asked.
  const [rawSearch, setRawSearch] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");
  /** Which row's copy button was last pressed, and whether it worked. */
  const [copyState, setCopyState] = useState<{ id: string; ok: boolean } | null>(null);

  useEffect(() => {
    setPage(1);
  }, [brandId]);

  useEffect(() => {
    // Nothing to debounce — including on mount, where this effect otherwise
    // scheduled an unconditional setPage(1) that landed ~300ms in and silently
    // reverted a Next pressed inside that window.
    const trimmed = rawSearch.trim();
    if (trimmed === q) return;
    const timer = window.setTimeout(() => {
      setQ(trimmed);
      // A new search is a new result set; staying on page 4 of the old one
      // would show an empty page and read as "no matches".
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [rawSearch, q]);

  // Reset so the outcome is a transient acknowledgement rather than a permanent
  // label. Cleared on unmount so a late timer cannot set state on a torn-down
  // component.
  useEffect(() => {
    if (!copyState) return;
    const timer = window.setTimeout(() => setCopyState(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copyState]);

  const invites = usePrequalificationInvites({
    page,
    pageSize: PAGE_SIZE,
    partnerBrandId: brandId,
    // Blank is dropped by the client's query builder either way; sending
    // undefined keeps an empty box indistinguishable from an unsent param.
    q: q || undefined,
    sort,
  });

  const resend = useResendPrequalificationInvite();
  const revoke = useRevokePrequalificationInvite();
  const busy = resend.isPending || revoke.isPending;
  const actionError = (resend.error ?? revoke.error) as Error | null | undefined;

  const totalPages = useMemo(() => {
    const data = invites.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [invites.data]);

  /**
   * A copy affordance that tells the truth.
   *
   * This previously optional-chained the write (`navigator.clipboard?.writeText?.(url)`)
   * and set "Copied" unconditionally, so a missing Clipboard API no-opped
   * silently while the button still claimed success, and nothing ever reset it.
   * The API is also unavailable on an insecure origin, which is exactly where a
   * staff member is most likely to be testing. Same fix as the site card's
   * public-link row.
   */
  const copyInviteLink = useCallback(async (invite: PrequalInviteDto) => {
    const write = navigator.clipboard?.writeText;
    if (!write) {
      setCopyState({ id: invite.id, ok: false });
      return;
    }
    try {
      await navigator.clipboard.writeText(invite.inviteUrl);
      setCopyState({ id: invite.id, ok: true });
    } catch {
      // Denied permission, or an insecure origin. Say so — a silent failure
      // here means a staff member pastes whatever was on the clipboard before.
      setCopyState({ id: invite.id, ok: false });
    }
  }, []);

  const columns: Column<PrequalInviteDto>[] = useMemo(() => {
    const now = Date.now();
    return [
      {
        key: "recipient",
        header: "Recipient",
        render: (i) => (
          <div>
            <div className="font-semibold">
              {i.firstName} {i.lastName}
            </div>
            <div className="text-caption text-gray-mid">{i.email}</div>
          </div>
        ),
      },
      {
        key: "role",
        header: "Role",
        render: (i) => (
          <Badge status={i.role === "Buyer" ? "info" : "gold"}>{i.role}</Badge>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (i) => (
          <div className="flex flex-wrap items-center gap-space-1">
            <Badge status={PREQUAL_INVITE_STATUS_BADGE[i.status]}>{i.status}</Badge>
            {/* An overlay on the real status, never a replacement for it — the
                server has no such status and the filters would disagree. */}
            {isStalled(i, now) && (
              <Badge
                status="review"
                title={`No movement in over ${STALL_DAYS} days`}
              >
                Stalled
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: "sent",
        header: "Sent",
        render: (i) => (
          <div className="text-caption">
            {date(i.sentAtUtc)}
            {i.sendCount > 1 && (
              <div className="text-gray-mid">Resent &times;{i.sendCount - 1}</div>
            )}
          </div>
        ),
      },
      {
        key: "expires",
        header: "Expires",
        render: (i) => <span className="text-caption">{date(i.expiresAtUtc)}</span>,
      },
      {
        key: "sender",
        header: "Sender",
        render: (i) => (
          <span className="text-caption text-gray-mid">{i.invitedByLabel ?? "—"}</span>
        ),
      },
      {
        key: "actions",
        header: "",
        className: "text-right",
        render: (i) => (
          <div className="flex items-center justify-end gap-space-1">
            <Button
              variant="tertiary"
              size="sm"
              aria-label={`Copy invitation link for ${i.email}`}
              onClick={() => void copyInviteLink(i)}
            >
              <Copy size={14} strokeWidth={1.5} aria-hidden />
              {copyState?.id === i.id &&
                (copyState.ok ? (
                  <span className="ml-space-1">Copied</span>
                ) : (
                  <span className="ml-space-1 text-status-error" role="alert">
                    Couldn&rsquo;t copy
                  </span>
                ))}
            </Button>
            {canManage && (
              <>
                {/* Disabled, not hidden — a control that vanishes leaves the
                    reader wondering whether it was ever there. The reason rides
                    the native `title` for now; WS7: replace with the shared
                    Tooltip primitive landing at
                    design-system/components/ui/Tooltip.tsx. */}
                <Button
                  variant="tertiary"
                  size="sm"
                  aria-label={`Resend invitation to ${i.email}`}
                  title={terminalInviteReason(i.status) ?? undefined}
                  disabled={busy || isTerminalInviteStatus(i.status)}
                  onClick={() => resend.mutate(i.id)}
                >
                  <RotateCw size={14} strokeWidth={1.5} aria-hidden />
                </Button>
                {/* "Revoke", matching the endpoint and the audit trail — this
                    is the same mutation the cross-book queue runs, and one
                    action must not carry two accessible names. */}
                <Button
                  variant="tertiary"
                  size="sm"
                  aria-label={`Revoke invitation to ${i.email}`}
                  title={terminalInviteReason(i.status) ?? undefined}
                  disabled={busy || isTerminalInviteStatus(i.status)}
                  onClick={() => revoke.mutate(i.id)}
                >
                  <Ban size={14} strokeWidth={1.5} aria-hidden />
                </Button>
              </>
            )}
          </div>
        ),
      },
    ];
  }, [canManage, copyState, copyInviteLink, busy, date, resend, revoke]);

  return (
    <section data-testid="prequal-site-invites" aria-label="Invitations">
      <div className="mb-space-3 flex flex-wrap items-center gap-space-2">
        {/* 🔴 No `label` prop — see the note on the completions panel. */}
        <SearchField
          className="min-w-[220px] flex-1"
          value={rawSearch}
          onChange={setRawSearch}
          placeholder="Search by name or email…"
        />
        <SortSelect
          value={sort}
          onChange={(next) => {
            setSort(next as PrequalSort);
            setPage(1);
          }}
          options={SORT_OPTIONS}
        />
      </div>

      {actionError && (
        <p className="mb-space-2 text-small text-status-error" role="alert">
          {actionError.message}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={invites.data?.data ?? []}
        loading={invites.isLoading}
        error={invites.isError ? (invites.error as Error).message : null}
        emptyMessage={
          q
            ? "No invitations match your search. Search looks at the recipient's name and email address — reference numbers aren't searchable."
            : "Nobody has been invited through this site yet."
        }
        rowKey={(i) => i.id}
      />

      {invites.data && invites.data.totalCount > 0 && (
        <Pagination
          page={invites.data.page}
          pageSize={invites.data.pageSize}
          totalCount={invites.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </section>
  );
}
